import axios from 'axios'
import {getAdminToken} from '../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

const adminApi = axios.create({withCredentials: true})
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export function getPayments(params: {
  search?: string
  filter?: string
  sort?: string
  page?: number
  limit?: number
  needs_charge_from?: string
  include_active?: boolean
  // Name/email/phone lookup for the "needs charge" list. When set, the server
  // ignores the billing-date window entirely (and includes deactivated
  // accounts), so any customer can be found and charged by hand.
  needs_charge_search?: string
}): Promise<{data: any}> {
  return adminApi.get(`${API_URL}/admin/payments`, {params})
}

export function chargeAccount(
  accountId: string,
  data?: {amount?: number; description?: string; period?: string; invoice_name?: string; installments?: number}
): Promise<{data: any}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/charge`, data || {})
}

export function updatePaymentSettings(
  accountId: string,
  data: {disable_auto_payment?: boolean; auto_charge_enabled?: boolean; payment_period?: string; billing_day?: number; monthly_payment_amount?: number}
): Promise<{data: any}> {
  return adminApi.put(`${API_URL}/admin/payments/${accountId}/settings`, data)
}

// Record a manual (bank transfer) payment already received: creates a bank-transfer
// invoice + payment and extends the trial/subscription period accordingly. No card charge.
export function recordManualPayment(
  accountId: string,
  data: {amount: number; payment_date: string; period: string; invoice_name: string; method?: 'bank_transfer' | 'bit'}
): Promise<{data: {success: boolean; amount?: number; period?: string; new_trial_end?: string; children_count?: number; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/manual-payment`, data)
}

export function updateTrialEnd(
  accountId: string,
  data: {new_date: string; reason?: string}
): Promise<{data: any}> {
  return adminApi.put(`${API_URL}/admin/payments/${accountId}/trial-end`, data)
}

export function getPaymentHistory(
  accountId: string,
  params?: {page?: number; limit?: number}
): Promise<{data: any}> {
  return adminApi.get(`${API_URL}/admin/payments/${accountId}/history`, {params})
}

export function addPaymentNote(
  accountId: string,
  note: string,
  options?: {create_ticket?: boolean; ticket_department?: string; ticket_priority?: string}
): Promise<{data: any}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/note`, {note, ...(options || {})})
}

export function updatePaymentNote(noteId: string, note: string): Promise<{data: any}> {
  return adminApi.put(`${API_URL}/admin/payments/note/${noteId}`, {note})
}

export function deletePaymentNote(noteId: string): Promise<{data: any}> {
  return adminApi.delete(`${API_URL}/admin/payments/note/${noteId}`)
}

// Shared account comment (important_notes) — the SAME store shown in the admin
// profile-switcher dropdown and on the account detail page. Adding one here
// keeps the comment identical across payments / switcher / account pages.
export function addAccountImportantNote(accountId: string, text: string): Promise<{data: {admin_notes: any[]}}> {
  return adminApi.post(`${API_URL}/admin/account-notes/${accountId}`, {text})
}

export function deleteAccountImportantNote(accountId: string, noteId: string): Promise<{data: {admin_notes: any[]}}> {
  return adminApi.delete(`${API_URL}/admin/account-notes/${accountId}/${noteId}`)
}

export function addPaymentMethod(
  accountId: string
): Promise<{data: {success: boolean; url?: string; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/add-payment-method`)
}

export function createPaymentLink(
  accountId: string,
  data: {amount: number; period?: string; days?: number; max_installments?: number; invoice_name?: string; description?: string}
): Promise<{data: {success: boolean; url?: string; amount?: number; period_label?: string; max_installments?: number; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/payment-link`, data)
}

export type CardcomTokenCandidate = {
  TranzactionId: number
  Token: string
  Amount: number
  CreateDate: string
  Last4: string
  CardMonth: number
  CardYear: number
  Brand?: string
  CardOwnerName?: string
  CardOwnerEmail?: string
  InvoiceName?: string
  DocumentNumber?: number
  DocumentUrl?: string
  already_added: boolean
  amount_matches: boolean
  name_matches?: boolean
}

export type PaymentMethod = 'credit_card' | 'cheque' | 'cash' | 'bank_transfer' | 'unknown'

export type AccountInvoiceMethod = {
  Invoice_Number: number
  InvoiceDate: string
  Amount: number
  Terminal_Number?: number
  Cust_Name?: string
  method: PaymentMethod
  DocUrl?: string
}

export type PaymentMethodsInfo = {
  recent_invoices: AccountInvoiceMethod[]
  has_credit_card_invoice: boolean
  non_cc_only: boolean
}

// List Cardcom transactions (with saved tokens) in a date range, so an admin can
// recover a card whose token was never stored on the account. Also returns the
// account's recent invoices with their detected payment method, so an empty
// candidate list explains itself (e.g. a client who pays by bank transfer).
export function getCardcomTokenCandidates(
  accountId: string,
  data: {fromDate?: string; toDate?: string; name?: string}
): Promise<{data: {success: boolean; monthly_amount?: number; count?: number; candidates?: CardcomTokenCandidate[]; payment_methods?: PaymentMethodsInfo; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/cardcom-token-candidates`, data)
}

// Attach a chosen transaction's saved token to the account.
export function addCardcomToken(
  accountId: string,
  transaction: CardcomTokenCandidate
): Promise<{data: {success: boolean; already?: boolean; payment_tokens?: any[]; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/add-cardcom-token`, {transaction})
}

// Put an older saved card back as the active one: the server moves that entry to
// the end of payment_tokens, which is the card every charge path uses.
export function setActivePaymentToken(
  accountId: string,
  data: {index: number; token?: string}
): Promise<{data: {success: boolean; already?: boolean; payment_tokens?: any[]; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/set-active-token`, data)
}

export function loginAsUser(
  accountId: string
): Promise<{data: {success: boolean; api_token?: string; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/accounts/${accountId}/login-as`, {})
}

export function bulkDisableAutoPayment(): Promise<{data: {success: boolean; modified_count: number; matched_count: number; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/bulk-disable-autopay`, {})
}

export function getAutoChargeEligible(): Promise<{data: {success: boolean; accounts: any[]; total_amount: number; error?: string}}> {
  return adminApi.get(`${API_URL}/admin/payments/auto-charge/eligible`)
}

export function lockAccount(accountId: string, reason: string): Promise<{data: {success: boolean; is_locked?: boolean; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/lock`, {reason})
}

export function unlockAccount(accountId: string): Promise<{data: {success: boolean; is_locked?: boolean; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/unlock`, {})
}

export function normalizeTrialEod(): Promise<{data: {success: boolean; scanned?: number; normalized?: number; alreadyEod?: number; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/normalize-trial-eod`, {})
}

// ---- Client WhatsApp messaging (gender + templates + history) ----

export type WaTemplate = {
  _id: string
  name: string
  male_text: string
  female_text: string
  createdAt?: string
}

export type WaMessage = {
  _id: string
  phone: string
  template_name?: string
  gender?: string
  message: string
  status?: string
  sent_by?: {first_name?: string; last_name?: string}
  createdAt: string
}

export function setClientGender(
  accountId: string,
  gender: 'male' | 'female' | null
): Promise<{data: {success: boolean; gender?: string | null; message?: string}}> {
  return adminApi.patch(`${API_URL}/admin/payments/${accountId}/gender`, {gender})
}

export function getWaTemplates(): Promise<{data: {success: boolean; templates: WaTemplate[]}}> {
  return adminApi.get(`${API_URL}/admin/payments/wa-templates`)
}

export function createWaTemplate(
  data: {name: string; male_text: string; female_text: string}
): Promise<{data: {success: boolean; template?: WaTemplate; message?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/wa-templates`, data)
}

export function updateWaTemplate(
  templateId: string,
  data: {name?: string; male_text?: string; female_text?: string}
): Promise<{data: {success: boolean; template?: WaTemplate; message?: string}}> {
  return adminApi.put(`${API_URL}/admin/payments/wa-templates/${templateId}`, data)
}

export function deleteWaTemplate(templateId: string): Promise<{data: {success: boolean; message?: string}}> {
  return adminApi.delete(`${API_URL}/admin/payments/wa-templates/${templateId}`)
}

export function sendClientWhatsapp(
  accountId: string,
  templateId: string
): Promise<{data: {success: boolean; message?: WaMessage | string}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/send-whatsapp`, {templateId})
}

export function getClientWaHistory(
  accountId: string
): Promise<{data: {success: boolean; messages: WaMessage[]}}> {
  return adminApi.get(`${API_URL}/admin/payments/${accountId}/wa-history`)
}

// ---- Bank transfer details ----
// One shared block of text every admin copies to customers paying by transfer.
export function getBankDetails(): Promise<{data: {success: boolean; details: string}}> {
  return adminApi.get(`${API_URL}/admin/payments/bank-details`)
}
export function saveBankDetails(details: string): Promise<{data: {success: boolean; details: string}}> {
  return adminApi.put(`${API_URL}/admin/payments/bank-details`, {details})
}

// ---- AI billing review (read-only opinion, never acts) ----

export type AiVerdict = 'charge' | 'set_price' | 'contact' | 'dont_charge' | 'review'

export type AiReview = {
  verdict: AiVerdict
  comment: string
  posted_14d?: number
  generated_at?: string
}

// Generate (or return cached) AI comments for the given accounts. Only accounts
// whose numbers changed since the last review actually hit the model.
export function generateAiReviews(
  accountIds: string[],
  force = false
): Promise<{data: {success: boolean; reviews: Record<string, AiReview>; generated: number; requested: number; errors?: string[]; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/ai-review`, {account_ids: accountIds, force})
}

// ---- "Need to contact" list ----

export type ContactTaskNote = {
  _id: string
  text: string
  admin?: {first_name?: string; last_name?: string}
  created_at: string
}

export type ContactTask = {
  _id: string
  account: any
  status: 'open' | 'done'
  reason?: string
  source?: 'manual' | 'ai'
  created_by?: {first_name?: string; last_name?: string}
  notes: ContactTaskNote[]
  snapshot?: {
    posted_14d?: number
    monthly_payment_amount?: number
    trial_period_end?: string
    has_card?: boolean
    last_payment_failed?: boolean
  }
  ai_review?: AiReview | null
  createdAt: string
  resolved_at?: string
  due_date?: string | null
  due_time?: string | null
  reminder?: number
  progress?: TaskProgress
}

export function getContactTasks(
  status: 'open' | 'done' | 'all' = 'open'
): Promise<{data: {success: boolean; tasks: ContactTask[]; open_count: number}}> {
  return adminApi.get(`${API_URL}/admin/payments/contact-tasks`, {params: {status}})
}

// dueDate ('YYYY-MM-DD') schedules the call. An account with an open task gets
// that task updated (already: true) — never a second one.
export function flagForContact(
  accountId: string,
  reason?: string,
  source: 'manual' | 'ai' = 'manual',
  dueDate?: string
): Promise<{data: {success: boolean; already?: boolean; task: ContactTask}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/contact-flag`, {reason, source, due_date: dueDate})
}

export function unflagContact(accountId: string): Promise<{data: {success: boolean}}> {
  return adminApi.delete(`${API_URL}/admin/payments/${accountId}/contact-flag`)
}

export function addContactTaskNote(
  taskId: string,
  text: string
): Promise<{data: {success: boolean; task: ContactTask}}> {
  return adminApi.post(`${API_URL}/admin/payments/contact-tasks/${taskId}/note`, {text})
}

export function deleteContactTaskNote(taskId: string, noteId: string): Promise<{data: {success: boolean}}> {
  return adminApi.delete(`${API_URL}/admin/payments/contact-tasks/${taskId}/note/${noteId}`)
}

export function resolveContactTask(taskId: string, note?: string): Promise<{data: {success: boolean}}> {
  return adminApi.post(`${API_URL}/admin/payments/contact-tasks/${taskId}/resolve`, {note})
}

export function reopenContactTask(taskId: string): Promise<{data: {success: boolean}}> {
  return adminApi.post(`${API_URL}/admin/payments/contact-tasks/${taskId}/reopen`, {})
}

// ---- Never-expiring payment links sent over WhatsApp (monthly + yearly) ----

export type LinkPricing = {
  monthly: number
  yearly: number
  yearly_multiplier: number
  monthly_installments: number
  yearly_installments: number
  message_male: string
  message_female: string
}

export type ResolvedPrices = {
  monthly: number
  yearly: number
  monthly_installments: number
  yearly_installments: number
  monthly_source: 'override' | 'account' | 'default'
  yearly_source: 'override' | 'default' | 'derived'
}

export type PriceOverride = {
  monthly?: number
  yearly?: number
  monthly_installments?: number
  yearly_installments?: number
}

export function getLinkPricing(): Promise<{data: {success: boolean; pricing: LinkPricing}}> {
  return adminApi.get(`${API_URL}/admin/payments/link-pricing`)
}

export function saveLinkPricing(pricing: Partial<LinkPricing>): Promise<{data: {success: boolean; pricing: LinkPricing}}> {
  return adminApi.put(`${API_URL}/admin/payments/link-pricing`, pricing)
}

// What this specific customer would be offered (override → their own rate → default),
// plus a preview of the message. Creates nothing.
export function getCustomerPaymentLinks(accountId: string): Promise<{data: {
  success: boolean
  prices: ResolvedPrices
  pricing: LinkPricing
  override: PriceOverride
  // The RAW message template, tokens intact — this is what the editor loads.
  template: string
  template_source: 'customer' | 'default_male' | 'default_female'
  has_custom_message: boolean
  // The same template rendered with this customer's prices, for display only.
  preview: string
  phone: string
  gender: 'male' | 'female' | null
  existing_links: Array<{_id: string; amount: number; period_label?: string; max_installments?: number; created_at: string; url: string}>
}}> {
  return adminApi.get(`${API_URL}/admin/payments/${accountId}/payment-links`)
}

// Save this customer's own message text (a TEMPLATE — keep {monthly_link} /
// {yearly_link} in it). An empty string clears it back to the global default.
export function saveCustomerMessage(
  accountId: string,
  message: string
): Promise<{data: {
  success: boolean
  template: string
  template_source: 'customer' | 'default_male' | 'default_female'
  has_custom_message: boolean
  preview: string
  missing_tokens: string[]
  error?: string
}}> {
  return adminApi.put(`${API_URL}/admin/payments/${accountId}/payment-links/message`, {message})
}

// Per-customer price override. A 0/empty value clears that field.
export function saveCustomerPrices(
  accountId: string,
  override: PriceOverride
): Promise<{data: {success: boolean; prices: ResolvedPrices; override: PriceOverride}}> {
  return adminApi.put(`${API_URL}/admin/payments/${accountId}/payment-links/prices`, override)
}

// Send BOTH never-expiring links (monthly + yearly) to the client on WhatsApp.
export function sendPaymentLinksWhatsapp(
  accountId: string,
  data?: {monthly?: number; yearly?: number; monthly_installments?: number; yearly_installments?: number; message?: string}
): Promise<{data: {
  success: boolean
  wa_message?: WaMessage
  message?: string
  prices?: ResolvedPrices
  links?: {monthly: string; yearly: string}
  error?: string
}}> {
  return adminApi.post(`${API_URL}/admin/payments/${accountId}/send-payment-links`, data || {})
}

// ---- Virtual-number lead activity ----
// Fetched lazily after the list renders: counting calls costs one PayCall
// request per number, so it must not block the payments page.

export type VnActivity = {
  numbers: Array<{ddi: string; forward_number?: string}>
  phone_calls: number
  whatsapp_leads: number
  marketplace_inquiries: number
  post_comments: number
  total_leads: number
  from: string
  to: string
}

export function getVirtualNumberActivity(
  accountIds: string[],
  opts?: {days?: number; skipPaycall?: boolean}
): Promise<{data: {success: boolean; activity: Record<string, VnActivity>; days: number}}> {
  return adminApi.post(`${API_URL}/admin/payments/vn-activity`, {
    account_ids: accountIds,
    days: opts?.days ?? 30,
    skip_paycall: opts?.skipPaycall ?? false,
  })
}

// ---- Payment tasks: calendar + per-account window ----

export type TaskProgress = 'new' | 'in_progress'

export type CalendarTask = {
  _id: string
  account: {_id: string; first_name?: string; last_name?: string; company_name?: string; phone?: string; monthly_payment_amount?: number; trial_period_end?: string}
  status: 'open' | 'done'
  progress: TaskProgress
  reason: string
  source?: 'manual' | 'ai'
  due_date: string | null
  due_time?: string | null  // 'HH:mm', Israel time
  reminder?: number         // minutes before due (-1 = off); untimed tasks count as 09:00
  notes_count: number
  last_note: string
  created_at: string
  resolved_at?: string | null
}

// from / to are inclusive 'YYYY-MM-DD' (Israel days), at most ~2 months apart.
export function getPaymentsCalendar(
  from: string,
  to: string
): Promise<{data: {success: boolean; tasks: CalendarTask[]; error?: string}}> {
  return adminApi.get(`${API_URL}/admin/payments/calendar`, {params: {from, to}})
}

// One client's tasks, open and done.
export function getAccountTasks(accountId: string): Promise<{data: {success: boolean; tasks: ContactTask[]}}> {
  return adminApi.get(`${API_URL}/admin/payments/contact-tasks`, {params: {status: 'all', account: accountId}})
}

// Always a new task (a client can have several). dueDate is 'YYYY-MM-DD'.
export function createContactTask(
  accountId: string,
  data: {reason?: string; due_date?: string; due_time?: string; reminder?: number}
): Promise<{data: {success: boolean; task: ContactTask; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/contact-tasks`, {account_id: accountId, ...data})
}

// null clears the date (task goes back to the unscheduled list).
export function setContactTaskDueDate(
  taskId: string,
  dueDate: string | null
): Promise<{data: {success: boolean; due_date: string | null; due_time: string | null; error?: string}}> {
  return adminApi.put(`${API_URL}/admin/payments/contact-tasks/${taskId}/due-date`, {due_date: dueDate})
}

// Edit reason / day / progress. Omitted fields are left as they are; due_date null clears it.
export function updateContactTask(
  taskId: string,
  data: {reason?: string; due_date?: string | null; due_time?: string | null; reminder?: number; progress?: TaskProgress}
): Promise<{data: {success: boolean; reason: string; due_date: string | null; due_time: string | null; reminder: number; progress: TaskProgress; error?: string}}> {
  return adminApi.put(`${API_URL}/admin/payments/contact-tasks/${taskId}`, data)
}

// Hard delete (a task added by mistake). Completing = resolveContactTask, which keeps history.
export function deleteContactTask(taskId: string): Promise<{data: {success: boolean; error?: string}}> {
  return adminApi.delete(`${API_URL}/admin/payments/contact-tasks/${taskId}`)
}

// ---- Reminders + templates ----

// snoozed_until is part of the "already notified" key, so a snoozed task rings again.
export type ReminderTask = CalendarTask & {due_at: string; remind_at: string; overdue: boolean; snoozed_until: string | null}

// Open tasks whose reminder time has come and that aren't snoozed.
export function getTaskReminders(): Promise<{data: {success: boolean; tasks: ReminderTask[]}}> {
  return adminApi.get(`${API_URL}/admin/payments/task-reminders`)
}

export function snoozeContactTask(
  taskId: string,
  by: {minutes: number} | {until: 'tomorrow'}
): Promise<{data: {success: boolean; snoozed_until: string; error?: string}}> {
  return adminApi.post(`${API_URL}/admin/payments/contact-tasks/${taskId}/snooze`, by)
}

export type TaskTemplate = {
  id: string
  text: string
  offset_days: number | null  // days from today; null = keep the day already chosen
  time: string | null         // 'HH:mm'
  reminder: number | null     // minutes; null = default
}

export function getTaskTemplates(): Promise<{data: {success: boolean; templates: TaskTemplate[]}}> {
  return adminApi.get(`${API_URL}/admin/payments/task-templates`)
}

// Replaces the whole shared list.
export function saveTaskTemplates(templates: Partial<TaskTemplate>[]): Promise<{data: {success: boolean; templates: TaskTemplate[]; error?: string}}> {
  return adminApi.put(`${API_URL}/admin/payments/task-templates`, {templates})
}
